import { MouseEvent } from "react";
import { Star } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

interface FavoriteButtonProps {
  favorite: boolean;
  onToggle: () => void;
  /** Outline button matching the other overview-header actions. */
  outlined?: boolean;
  className?: string;
}

/** Favorite toggle for a card, list row, or overview header. Suppresses the
 *  click so it works inside a clickable parent (a `<Link>` or an onClick row);
 *  the star fills when favorited. */
export const FavoriteButton = ({
  favorite,
  onToggle,
  outlined = false,
  className,
}: FavoriteButtonProps) => {
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  // Hover washes the interior in at partial strength to preview the press. The
  // group is named so hovering the surrounding card or row doesn't trigger it.
  const star = (
    <Star
      className={cn(
        "size-4 transition-colors",
        favorite
          ? "fill-amber-400 text-amber-500"
          : "fill-transparent group-hover/favorite:fill-amber-400/40 group-hover/favorite:text-amber-500"
      )}
      aria-hidden="true"
    />
  );

  const label = favorite ? "Unfavorite" : "Favorite";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {outlined ? (
          <Button
            variant="outline"
            size="icon"
            aria-label={label}
            aria-pressed={favorite}
            onClick={handleClick}
            className={cn("group/favorite shadow-xs", className)}
          >
            {star}
          </Button>
        ) : (
          <button
            type="button"
            aria-label={label}
            aria-pressed={favorite}
            onClick={handleClick}
            className={cn(
              "group/favorite inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground",
              "transition-colors hover:bg-muted hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]",
              className
            )}
          >
            {star}
          </button>
        )}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};
